import videoGenerationService, {
  AsyncVideoGenerateResponse,
  VideoDurationSeconds,
  VideoGenerationJob,
  VideoJobReferenceImage,
  VideoModelType,
  VideoReferenceImageInput,
} from '../../services/api/videoGenerationService';

/**
 * 失敗したジョブを、保存されている入力そのままで作り直す。
 *
 * 送信時にフォームを全消去しているため、失敗した利用者は同じ内容を手で打ち直していた。
 * 詳細（show）が返す入力値だけで生成 API を組み立て直す。
 */

/** 元の設定から差し替える項目。渡さなかった項目は元のジョブの値を使う。 */
export interface VideoJobResubmitOverrides {
  reference_images?: VideoReferenceImageInput[];
  video_model?: VideoModelType;
}

const REFERENCE_TYPES = ['brand_logo', 'product_image', 'material'] as const;

type ReferenceType = (typeof REFERENCE_TYPES)[number];

const isReferenceType = (value: string | undefined): value is ReferenceType =>
  !!value && (REFERENCE_TYPES as readonly string[]).includes(value);

/**
 * 保存済みの参照画像を生成 API が受け取る形へ戻す。
 *
 * 保存時に `type` は `category` へ写されるので読み替える。`category` が 'unknown' の
 * ものは型を付けずに送る（バックエンドは nullable。無効な値を送ると 422 になる）。
 */
export const toResubmitReferenceImages = (
  images: VideoJobReferenceImage[] | undefined
): VideoReferenceImageInput[] =>
  (images ?? []).flatMap((image): VideoReferenceImageInput[] => {
    const source = image.type ?? image.category;
    const type = isReferenceType(source) ? source : undefined;

    if (image.s3_key) return [{ ...(type ? { type } : {}), s3_key: image.s3_key }];
    if (image.image_data) return [{ ...(type ? { type } : {}), image_data: image.image_data }];

    return [];
  });

const SUBMITTABLE_DURATIONS: VideoDurationSeconds[] = [5, 6, 8, 12, 15, 20, 25, 30];

/**
 * ジョブに保存された秒数・解像度を、生成 API が受け付ける値へ絞り込む。
 * 収まらない値（列挙から外したあとの過去データ）は送らず、既定へ倒す。
 */
const toSubmittableDuration = (seconds: number | undefined): VideoDurationSeconds | undefined =>
  SUBMITTABLE_DURATIONS.find((value) => value === seconds);

const toSubmittableResolution = (resolution: string | undefined): '720p' | '1080p' | undefined =>
  resolution === '720p' || resolution === '1080p' ? resolution : undefined;

/**
 * 元のジョブの生成元。API が受け付けるのは video_ads / image_generation だけで、
 * UGC・歌広告などのクリップは専用の経路から作られるためここでは送らない
 * （送ると 422 になり、既定の video_ads へ倒すと履歴の分類が壊れる）。
 */
const resubmittableSource = (job: VideoGenerationJob) =>
  job.generation_source === 'video_ads' || job.generation_source === 'image_generation'
    ? job.generation_source
    : undefined;

export const resubmitVideoJob = async (
  job: VideoGenerationJob,
  overrides: VideoJobResubmitOverrides = {}
): Promise<AsyncVideoGenerateResponse> => {
  const referenceImages =
    overrides.reference_images ?? toResubmitReferenceImages(job.reference_images);

  const response = await videoGenerationService.generateAsync(
    job.prompt,
    (job.aspect_ratio as '16:9' | '9:16' | '1:1') || '16:9',
    referenceImages.length > 0 ? referenceImages : undefined,
    toSubmittableDuration(job.duration_seconds),
    toSubmittableResolution(job.resolution),
    overrides.video_model ?? job.video_model,
    resubmittableSource(job),
    undefined,
    job.generate_audio
  );

  if (!response.success) {
    throw new Error(response.message || '動画生成ジョブの開始に失敗しました');
  }

  return response;
};
